import _ from 'lodash'
import React from 'react'
import PropTypes from 'prop-types'
import userHasPrivs from 'Helper/Auth/userHasPrivs'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import rootActions from 'actions/rootActions'
import { push } from 'Helper/routeHelpers'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import StandardModalContent from '../StandardModalContent/StandardModalContent.jsx'
import Store from '../../../store'
import { compatibleWithCurrentScreenType } from 'Helper/screenTypeHelpers'


class PatientSearch extends React.PureComponent {
  static searchDebounceMS = 500
  static pushUrl = (url, appConfig, patient) => {
    let type
    if (url.indexOf('/dashboard/patient') !== -1) {
      // on patient screen so use search action
      type = 'patientDisplay'
    } else if (url.indexOf('/dashboard/admin') !== -1) {
      type = 'adminDisplay'
    } else {
      type = 'nurseDashboard'
    }
    const searchConfig = PatientSearch.convertLegacySearch(type, appConfig)
    let path = (searchConfig.find(compatibleWithCurrentScreenType) || {})?.action
    if (path && path.indexOf(':patientId') != -1) {
      path = path.replace(':patientId', patient.patient_id)
    } else {
      // if nothing has been defined, just go to patient screen.
      path = '/dashboard/patient/' + patient.patient_id
    }
    // store the patient to the patient search history
    Store.dispatch({ type: 'PATIENT_SEARCH_HISTORY_PUSH', patient })
    push(path, false)
  }
  static convertLegacySearch = (type, appConfig) => {
    // previously search action was a {action: '...'} but now its a [{action: '...',screenType: '..'},{...}]
    let searchAction = appConfig[type].searchAction || []
      if(typeof searchAction === 'object' && !Array.isArray(searchAction)) {
        //legacy value, convert to array
        if(Object.keys(searchAction).length > 0) {
          searchAction = [{...searchAction, screenType: 'standard'}]
        } else {
          // some legacy tools make search action an empty object to "clear" it. In the new way of working these should be empty arrays
          searchAction = []
        }
      }
    return searchAction
  }
  queueUpAPI = (searchObject, callbacks, immediate = false) => {
    const modifiedCallbacks = { ...callbacks }
    // modify the callbacks to clear search info from state when the api
    // call returns so this can be used to show and clear the spinner correctly
    modifiedCallbacks[200] = (response, action, defaultCallback) => {
      this.setState({ pendingSearch: null })
      if (callbacks[200]) {
        callbacks[200](response, action, defaultCallback)
      }
    }
    modifiedCallbacks[500] = (response, action, defaultCallback) => {
      this.setState({ pendingSearch: null })
      if (callbacks[500]) {
        callbacks[500](response, action, defaultCallback)
      }
    }
    // every api call updates pendingSearch to include the details of that call instate
    this.setState(
      { pendingSearch: { searchObject, callbacks: modifiedCallbacks } },
      // each time we make an api call... we will store the most recent api query info
      // in state then call executeAPI which is the debounced api... when it finally
      // runs it will only run once and pull the most recent details from state.
      immediate ? this.executeAPI : this.debouncedAPI
    )
  }
  static buildPatientString = patient => {
    let patientString = ''
    patientString +=
      patient.patient_id == null
        ? patient.lastname
        : `id: ${patient.patient_id} - ${patient.lastname}`
    patientString += patient.firstname == null ? '' : ', ' + patient.firstname
    patientString += patient.mi == null ? '' : ', ' + patient.mi
    patientString += patient.chart == null ? '' : ', ' + patient.chart
    patientString += patient.dob == null ? '' : ', ' + patient.dob
    patientString +=
      patient.account != null && patient.account.name != null
        ? ` - ${patient.account.name}`
        : ''
    return patientString
  }
  initialState = {
    searchExpanded: false,
    searchTerm: '',
    result: [],
    distinct: '',
    count: 0,
    distinctCount: 0,
    placeholder: 'Patient search',
    pendingSearch: null
  }
  constructor(props) {
    super(props)

    this.executeAPI = () => {
      this.debouncedAPI.cancel()
      if (this.state.pendingSearch) {
        this.props.xtractAPI(
          'patient',
          'SEARCH',
          this.state.pendingSearch.searchObject,
          this.state.pendingSearch.callbacks
        )
      }
    }

    // this is the debounced version of the api call
    this.debouncedAPI = _.debounce(
      this.executeAPI,
      PatientSearch.searchDebounceMS
    )

    // privileges
    this.allowCreatePatient = userHasPrivs('create_patients')
    this.allowPatientAccess = userHasPrivs('allow_patient_access')

    this.state = {
      ...this.initialState
    }
  }
  searchModal = React.createRef()
  searchInput = React.createRef()
  checkNumChk = (immediate = false) => {
    // See if this is a NumChk number (patient_id, rx_num, local barcode)
    const value = this.state.searchTerm.trim()
    if (
      value.match(/^\d+$/) ||
      value.match(/^LPO\d+T\d+PT\d+V-?\d+[A-Z]{0,2}D\d+$/i)
    ) {
      const callbacks = {
        200: result => {
          this.apiHandler(result.data, /* isNumChk */ true, /* autoSelect */ true)
        }
      }
      this.queueUpAPI({ number: this.state.searchTerm }, callbacks, immediate)
    } else if (this.state.result.length === 1) {
      this.selectPatient(this.state.result[0])
    }
  }
  patientClick = Row => {
    if (Row.patient_id) {
      this.selectPatient(Row)
    } else if (Row.patient && this._mounted) {
      // only add a comma at the end of the search
      // if the last thing isn't dob
      const splitPt = Row.patient.toString().split(',')
      const searchInput = splitPt.length === 5 ? Row.patient : Row.patient + ','

      this.searchInputChange(searchInput)
      this.searchInput.current.focus()
    }
  }
  searchUpdated = e => {
    const key = e.which
    if (key === 13) {
      // Enter - the user is done entering data
      this.checkNumChk(true)
    }
  }
  apiHandler = (value, isNumChk = false, autoSelect = false) => {
    if (this._mounted === true) {
      const { matches = [] } = value.data.patient
      const result = matches.reduce(
        (total, patient) => {
          // new element
          const patientString = PatientSearch.buildPatientString(patient)
          if (total.some(pt => pt.patient === patientString)) {
            // already in the array
            return total
          } else {
            return [...total, { patient: patientString, ...patient }]
          }
        },
        isNumChk ? [...this.state.result] : []
      )
      if (result.length === 1 && autoSelect) {
        this.selectPatient(result[0])
      } else {
        if (isNumChk) {
          this.setState({
            result,
            count: result.length
          })
        } else {
          this.setState({
            result,
            count: value.data.patient.count,
            distinct: value.data.patient.distinct,
            distinctCount: value.data.patient.distinct_count
          })
        }
      }
    }
  }
  componentDidMount() {
    this._mounted = true
    this.searchInput.current.addEventListener('keyup', this.searchUpdated)
  }
  componentDidUpdate() {
    if (
      this.state.searchExpanded === false &&
      this.state.placeholder !== 'Patient search'
    ) {
      this.setState({ placeholder: 'Patient search' })
    }
  }
  selectPatient = patient => {
    this.setState({ ...this.initialState })
    this.searchInput.current.blur() // so it can gain focus again later
    if (this.props.clickHandler != null) {
      // If the component was given a clickAction prop, select the patient by calling the click action and passing it the patient
      this.props.clickHandler(patient)
    } else {
      // if the component wasn't given a clickAction prop, do the default action which is to forward to the correct patient url
      if (patient != null && this.props.match != null) {
        PatientSearch.pushUrl(
          this.props.match.url,
          this.props.appConfig,
          patient
        )
      }
    }
  }
  createPatient = () => {
    if (!this.allowCreatePatient) {
      this.props.openModal('create_patients_privilege_modal', {
        title: 'Insufficient privilege to perform action ',
        content: (
          <StandardModalContent
            messageContent={
              'The Create Patients privilege within Global is required to perform this action'
            }
            okHandler={() => {
              this.props.closeModal('create_patients_privilege_modal')
            }}
            okText={'OK'}
          />
        ),
        closeable: true
      })
      return
    }
    const patient = this.state.searchTerm.split(',')
    // trim is to remove any white spaces from the beginning or end of a name,
    // but allows spaces inbetween for multi-name names (gen2 issue 6810)
    let lastname = patient[0] ? patient[0].trim() : ''
    let firstname = patient[1] ? patient[1].trim() : ''
    let mi = patient[2] ? patient[2].trim() : ''
    const chart = patient[3] ? patient[3].trim() : ''
    const dob = patient[4] ? patient[4].trim() : ''

    // capitolize the first letter of each name; names seperated by either a space, single quote, or hyphen
    lastname = lastname.replace(/(\b[a-z](?!\s))/g, str => str.toUpperCase())
    firstname = firstname.replace(/(\b[a-z](?!\s))/g, str => str.toUpperCase())
    mi = mi.replace(/(\b[a-z](?!\s))/g, str => str.toUpperCase())

    const displayname =
      firstname != '' && lastname != ''
        ? `${firstname.substr(0, 1)}. ${lastname.substr(0, 1)}.`
        : ''
    const newPatient = {
      lastname,
      firstname,
      mi,
      chart,
      dob,
      displayname
    }
    const { nurseDashboard = {} } = this.props.appConfig
    const { searchAction = {} } = nurseDashboard
    const path =
      searchAction.action != null
        ? searchAction.action
        : '/dashboard/patient/:patientId'
    this.props.createPatientHandler(newPatient, path)
  }
  searchInputChange = searchTerm => {
    this.setState({ searchTerm })
    const callbacks = {
      200: result => {
        this.apiHandler(result.data)
      }
    }
    this.queueUpAPI({ search: searchTerm }, callbacks)
  }
  searchContent = () => {
    const centerStyle = {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }
    if (this.state.result.length !== 0 && this.state.searchExpanded) {
      return this.state.result.map((row, idx) => (
        <div
          className={'search-result'}
          key={idx}
          onClick={() => this.patientClick(row)}
        >
          {row.patient}
        </div>
      ))
    } else if (this.state.pendingSearch != null && this.state.searchExpanded) {
      return (
        <div style={centerStyle}>
          <FontAwesomeIcon
            icon={['fas', 'circle-notch']}
            size="2x"
            style={{ color: 'black' }}
            spin
            fixedWidth
          />
        </div>
      )
    } else if (this.state.count > 10 && this.state.searchExpanded) {
      return (
        <div style={centerStyle}>
          {`Too many results to display. ${this.state.count} patients match this search.`}
        </div>
      )
    } else {
      return null
    }
  }
  render() {
    let footerText =
      this.state.distinctCount + ' distinct ' + this.state.distinct
    footerText +=
      this.state.distinctCount > 1 ? ' matches out of ' : 'match out of '
    footerText += this.state.count
    footerText += this.state.count > 1 ? ' total patients.' : ' total patient.'
    const disabledClass = this.props.disabled ? ' disabled' : ''
    let patientSearchModalClass = 'patient-search-modal'
    if (this.state.searchExpanded) {
      patientSearchModalClass += ' expanded'
    }

    return (
      <div
        className={`patient-search-container${disabledClass}`}
        aria-disabled={this.props.disabled}
      >
        {this.state.searchExpanded
          ? (
          <div
            className="invisible-cover"
            onClick={() => this.setState({ searchExpanded: false })}
          />
            )
          : null}
        <div ref={this.searchModal} className={patientSearchModalClass}>
          <div
            className="input-row"
            data-toggle={this.allowPatientAccess ? null : 'tooltip'}
            data-title={
              'The Allow Patient Access privilege within Global is required to perform this action'
            }
            data-placement={'bottom'}
          >
            <input
              ref={this.searchInput}
              type="text"
              disabled={this.props.disabled || !this.allowPatientAccess}
              aria-label="Patient search input"
              className="search-input"
              value={this.state.searchTerm}
              onChange={e => this.searchInputChange(e.target.value)}
              onFocus={() => {
                this.setState({
                  searchExpanded: true,
                  placeholder: 'Lastname, Firstname, MI, Chart, DOB'
                })
              }}
              placeholder={this.state.placeholder}
            />
            {this.state.searchExpanded
              ? (
              <FontAwesomeIcon
                icon={['fas', 'times-circle']}
                className="close-search"
                onClick={() => this.setState({ searchExpanded: false })}
              />
                )
              : null}
          </div>
          {this.searchContent()}
          {this.props.allowCreatePatient === 'T' &&
          this.props.createPatientHandler != null &&
          this.state.searchTerm != '' &&
          this.state.searchExpanded
            ? (
            <div className="btn btn-success" onClick={this.createPatient}>
              Create patient
            </div>
              )
            : null}
          {this.state.result.length !== 0 &&
          this.state.count !== 1 &&
          this.state.searchExpanded
            ? (
            <div className="patient-search-footer">{footerText}</div>
              )
            : null}
        </div>
      </div>
    )
  }
  componentWillUnmount() {
    this.searchInput.current.removeEventListener('keyup', this.searchUpdated)
    this._mounted = false
  }
}

PatientSearch.propTypes = {
  disabled: PropTypes.bool
}

const mapStateToProps = state => {
  return {
    appConfig: state.appConfig,
    allowCreatePatient: state.appConfig.allowCreatePatient,
    windowContainer: state.interactableState.container,
    ...state.router
  }
}

const mapDispatchToProps = dispatch => {
  return bindActionCreators(rootActions, dispatch)
}

const mergeProps = (state, actions, props) => ({
  ...state,
  ...actions,
  ...props
})

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(PatientSearch)
